import { RefreshCw } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { formatLongDate } from "../gulaConstants";
import { Field } from "../SimpleSelect";
import type { AccSettings } from "./accounting";
import { metaStatus, metaSync, type MetaStatus } from "./accountingApi";

const isEmailList = (text: string) =>
  text
    .split(/[,;\s]+/)
    .filter(Boolean)
    .every((value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value));

/** The accountant's email and the Meta ad accounts read every morning. */
export const AccountingSettingsDialog = ({
  open,
  onOpenChange,
  settings,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings?: AccSettings;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cc, setCc] = useState("");
  const [accounts, setAccounts] = useState("");
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<MetaStatus | null>(null);
  const [metaBusy, setMetaBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(settings?.gestor_name ?? "");
    setEmail(settings?.gestor_email ?? "");
    setCc(settings?.gestor_cc ?? "");
    setAccounts((settings?.meta_ad_account_ids ?? []).join(", "));
    setMeta(null);
  }, [open, settings]);

  const save = async () => {
    if (email.trim() && !isEmailList(email))
      return notify("El email del gestor no es válido", { type: "warning" });
    if (cc.trim() && !isEmailList(cc))
      return notify("Algún email en copia no es válido", { type: "warning" });
    setSaving(true);
    try {
      const data = {
        gestor_name: name.trim() || null,
        gestor_email: email.trim() || null,
        gestor_cc: cc.trim() || null,
        meta_ad_account_ids: accounts
          .split(/[,;\s]+/)
          .map((id) => id.replace(/^act_/, "").trim())
          .filter((id) => /^\d+$/.test(id)),
        updated_at: new Date().toISOString(),
      };
      await dataProvider.update("gula_acc_settings", {
        id: 1,
        data,
        previousData: settings ?? { id: 1 },
      });
      notify("Ajustes guardados", { type: "success" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      notify("No se han podido guardar los ajustes", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const check = async () => {
    setMetaBusy(true);
    try {
      setMeta(await metaStatus());
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    } finally {
      setMetaBusy(false);
    }
  };

  const syncNow = async () => {
    setMetaBusy(true);
    try {
      const result = await metaSync(`${new Date().getFullYear() - 1}-01-01`);
      notify(
        `Meta: ${result.inserted} meses nuevos, ${result.updated} actualizados`,
        { type: "success" },
      );
      onSaved();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    } finally {
      setMetaBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajustes de contabilidad</DialogTitle>
          <DialogDescription>
            A quién se manda el paquete y qué cuentas de Meta se leen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold">Gestor</h3>
          <Field label="Nombre">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              placeholder="gestoria@ejemplo.es"
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="En copia (opcional, separados por comas)">
            <Input value={cc} onChange={(event) => setCc(event.target.value)} />
          </Field>

          <h3 className="mt-2 text-sm font-semibold">Meta Ads</h3>
          <Field label="Cuentas publicitarias (ID)">
            <Input
              value={accounts}
              placeholder="1375808024214462"
              onChange={(event) => setAccounts(event.target.value)}
            />
          </Field>
          <p className="text-xs text-muted-foreground">
            Cada mañana el CRM apunta el gasto de cada mes como un gasto de
            «Meta Platforms Ireland Ltd» (inversión del sujeto pasivo). Los
            recibos de Meta que subas se añaden a ese mismo gasto.
            {settings?.meta_last_sync_at
              ? ` Última lectura: ${formatLongDate(settings.meta_last_sync_at)}.`
              : " Aún no se ha leído nunca."}
          </p>
          {settings?.meta_last_error ? (
            <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
              Último error: {settings.meta_last_error}
            </p>
          ) : null}
          {meta ? (
            <ul className="text-xs">
              {meta.accounts.map((account) => (
                <li
                  key={account.id}
                  className={
                    account.ok
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-destructive"
                  }
                >
                  {account.ok
                    ? `✓ ${account.name} (${account.currency})`
                    : `✗ ${account.id}: ${account.error}`}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={metaBusy}
              onClick={check}
            >
              Comprobar conexión
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={metaBusy}
              onClick={syncNow}
            >
              <RefreshCw className="h-4 w-4" />
              Leer gasto ahora
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button disabled={saving} onClick={save}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
